import type {
  AccessLevel,
  AccessOverlay,
  BoardSummary,
  CostCenter,
  CreateBoardInput,
  CreateTicketInput,
  CreateUserInput,
  Currency,
  EmploymentType,
  FieldSet,
  RecruitmentLanguage,
  ResetUserPasswordInput,
  TicketPriority,
  TicketStatus,
  UpdateBoardInput,
  CreateDepartmentInput,
  UpdateDepartmentInput,
  CreateJobRoleInput,
  UpdateJobRoleInput,
  UpdateTicketInput,
  UpdateUserInput,
  WorkflowDefinition,
  WorkflowProblem,
} from '../shared/index.js';

import { buildQuery, request } from './client.js';

export interface SessionUser {
  readonly id: string;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly jobRoleSlug: string;
  readonly jobRoleId: string;
  readonly overlays: readonly AccessOverlay[];
  readonly departments: ReadonlyArray<{
    readonly departmentId: string;
    readonly slug: string;
    readonly level: AccessLevel;
  }>;
  readonly managedProjectIds: readonly string[];
  readonly mustChangePassword: boolean;
}

export interface Ticket {
  readonly id: string;
  readonly number: number;
  readonly boardId: string;
  readonly boardSlug: string;
  readonly boardName: string;
  readonly fieldSet: FieldSet;
  readonly title: string;
  readonly description: string | null;
  readonly status: TicketStatus;
  readonly priority: TicketPriority;
  readonly departmentId: string;
  readonly departmentSlug: string;
  readonly requesterId: string;
  readonly assigneeId: string | null;
  readonly projectId: string | null;
  readonly stageId: string;
  readonly stageSlug: string;
  readonly stageLabel: string;
  readonly boardPosition: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly closedAt: string | null;
  readonly archivedAt: string | null;

  readonly costCenter: CostCenter | null;
  readonly estimatedAmount: number | null;
  readonly currency: Currency | null;
  readonly neededBy: string | null;
  readonly deliveryLocation: string | null;
  readonly projectNote: string | null;

  readonly position: string | null;
  readonly language: RecruitmentLanguage | null;
  readonly deadline: string | null;
  readonly employmentType: EmploymentType | null;
  readonly numberOfPeople: number | null;
}

export interface PageMeta {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly totalPages: number;
}

export interface Comment {
  readonly id: string;
  readonly ticketId: string;
  readonly body: string;
  readonly isInternal: boolean;
  readonly createdAt: string;
  readonly author: { readonly id: string; readonly firstName: string; readonly lastName: string };
}

export interface Department {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly description: string | null;
  readonly isActive: boolean;
  readonly isSystem: boolean;
  readonly memberCount: number;
  readonly openTicketCount: number;
}

export interface JobRole {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly description: string | null;
  readonly isActive: boolean;
  readonly departmentId: string | null;
  readonly departmentName: string | null;
  readonly userCount: number;
}

export interface Project {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly clientName: string | null;
  readonly isActive: boolean;
  readonly projectManagerId: string | null;
  readonly divisionManagerId: string | null;
}

export interface DirectoryUser {
  readonly id: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly jobRoleId: string;
  readonly jobRoleName: string;
  readonly homeDepartmentId: string | null;
  readonly homeDepartmentName: string | null;
}

export interface AssignableUser {
  readonly id: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly jobRoleId: string;
  readonly jobRoleName: string;
}

export interface BoardWithWorkflow {
  readonly board: BoardSummary;
  readonly workflow: WorkflowDefinition;
  readonly problems: readonly WorkflowProblem[];
  readonly ticketCount: number;
  readonly liveTicketCount: number;
}

export interface BoardCard {
  readonly id: string;
  readonly number: number;
  readonly title: string;
  readonly description: string | null;
  readonly status: TicketStatus;
  readonly priority: TicketPriority;
  readonly departmentId: string;
  readonly departmentSlug: string;
  readonly requesterId: string;
  readonly assigneeId: string | null;
  readonly projectId: string | null;
  readonly stageId: string;
  readonly stageSlug: string;
  readonly stageLabel: string;
  readonly boardPosition: string;
  readonly createdAt: string;
}

export interface BoardView {
  readonly board: {
    readonly id: string;
    readonly slug: string;
    readonly name: string;
    readonly description: string | null;
    readonly fieldSet: FieldSet;
    readonly allowManualStatusChange: boolean;
  };
  readonly workflow: WorkflowDefinition;
  readonly items: readonly BoardCard[];
}

export interface TicketFilters {
  readonly boardId?: string;
  readonly status?: TicketStatus;
  readonly priority?: TicketPriority;
  readonly stageId?: string;
  readonly departmentId?: string;
  readonly assigneeId?: string;
  readonly requesterId?: string;
  readonly projectId?: string;
  readonly search?: string;
  readonly includeClosed?: boolean;
  readonly page?: number;
  readonly pageSize?: number;
}

export interface CardPlacement {
  readonly stageId: string;
  readonly beforeTicketId?: string;
  readonly afterTicketId?: string;
}

export const authApi = {
  login: (email: string, password: string) =>
    request<SessionUser>('/api/auth/login', { method: 'POST', body: { email, password } }),
  me: () => request<SessionUser>('/api/auth/me'),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  forgotPassword: (email: string) =>
    request<{ ok: true }>('/api/auth/forgot-password', { method: 'POST', body: { email } }),
  resetPassword: (token: string, newPassword: string) =>
    request<{ ok: true }>('/api/auth/reset-password', {
      method: 'POST',
      body: { token, newPassword },
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/api/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    }),
};

export const boardApi = {
  list: (includeInactive = false) =>
    request<{ items: readonly BoardWithWorkflow[] }>(
      `/api/boards${buildQuery({ includeInactive })}`,
    ),
  get: (boardId: string) => request<BoardWithWorkflow>(`/api/boards/${boardId}`),
  create: (input: CreateBoardInput) =>
    request<BoardWithWorkflow>('/api/boards', { method: 'POST', body: input }),
  update: (boardId: string, input: UpdateBoardInput) =>
    request<BoardWithWorkflow>(`/api/boards/${boardId}`, { method: 'PATCH', body: input }),
  remove: (boardId: string) => request<void>(`/api/boards/${boardId}`, { method: 'DELETE' }),
  reorder: (boardIds: readonly string[]) =>
    request<{ items: readonly BoardWithWorkflow[] }>('/api/boards/order', {
      method: 'PUT',
      body: { boardIds },
    }),
  createStage: (boardId: string, input: Record<string, unknown>) =>
    request<BoardWithWorkflow>(`/api/boards/${boardId}/stages`, { method: 'POST', body: input }),
  updateStage: (boardId: string, stageId: string, input: Record<string, unknown>) =>
    request<BoardWithWorkflow>(`/api/boards/${boardId}/stages/${stageId}`, {
      method: 'PATCH',
      body: input,
    }),
  reorderStages: (boardId: string, stageIds: readonly string[]) =>
    request<BoardWithWorkflow>(`/api/boards/${boardId}/stages/order`, {
      method: 'PUT',
      body: { stageIds },
    }),
  removeStage: (boardId: string, stageId: string) =>
    request<BoardWithWorkflow>(`/api/boards/${boardId}/stages/${stageId}`, { method: 'DELETE' }),
  createTransition: (boardId: string, input: Record<string, unknown>) =>
    request<BoardWithWorkflow>(`/api/boards/${boardId}/transitions`, {
      method: 'POST',
      body: input,
    }),
  updateTransition: (boardId: string, transitionId: string, input: Record<string, unknown>) =>
    request<BoardWithWorkflow>(`/api/boards/${boardId}/transitions/${transitionId}`, {
      method: 'PATCH',
      body: input,
    }),
  removeTransition: (boardId: string, transitionId: string) =>
    request<BoardWithWorkflow>(`/api/boards/${boardId}/transitions/${transitionId}`, {
      method: 'DELETE',
    }),
};

export const ticketApi = {
  list: (filters: TicketFilters) =>
    request<{ items: readonly Ticket[]; meta: PageMeta }>(`/api/tickets${buildQuery(filters)}`),
  board: (boardId: string, archived: 'exclude' | 'only' = 'exclude') =>
    request<BoardView>(`/api/tickets/board${buildQuery({ boardId, archived })}`),
  get: (id: string) => request<Ticket>(`/api/tickets/${id}`),
  create: (input: CreateTicketInput) =>
    request<Ticket>('/api/tickets', { method: 'POST', body: input }),
  update: (id: string, input: UpdateTicketInput) =>
    request<Ticket>(`/api/tickets/${id}`, { method: 'PATCH', body: input }),
  runTransition: (id: string, transitionId: string, note?: string) =>
    request<Ticket>(`/api/tickets/${id}/transitions`, {
      method: 'POST',
      body: note === undefined ? { transitionId } : { transitionId, note },
    }),
  move: (id: string, placement: CardPlacement) =>
    request<Ticket>(`/api/tickets/${id}/move`, { method: 'POST', body: placement }),
  remove: (id: string) => request<void>(`/api/tickets/${id}`, { method: 'DELETE' }),
  events: (id: string) => request<{ items: readonly TicketEvent[] }>(`/api/tickets/${id}/events`),
  archive: (id: string) => request<Ticket>(`/api/tickets/${id}/archive`, { method: 'POST' }),
  unarchive: (id: string) => request<Ticket>(`/api/tickets/${id}/unarchive`, { method: 'POST' }),
};

export interface Attachment {
  readonly id: string;
  readonly ticketId: string;
  readonly fileName: string;
  readonly contentType: string;
  readonly byteSize: number;
  readonly createdAt: string;
  readonly uploadedById: string;
  readonly uploadedByName: string;
}

export interface TicketEvent {
  readonly id: string;
  readonly type: string;
  readonly actor: {
    readonly id: string;
    readonly firstName: string;
    readonly lastName: string;
  } | null;
  readonly fromValue: string | null;
  readonly toValue: string | null;
  readonly detail: string | null;
  readonly createdAt: string;
}

export const attachmentApi = {
  list: (ticketId: string) =>
    request<{ items: readonly Attachment[] }>(`/api/tickets/${ticketId}/attachments`),
  upload: (ticketId: string, file: File) => {
    const body = new FormData();
    body.append('file', file, file.name);

    return request<Attachment>(`/api/tickets/${ticketId}/attachments`, {
      method: 'POST',
      formData: body,
    });
  },
  remove: (attachmentId: string) =>
    request<void>(`/api/tickets/attachments/${attachmentId}`, { method: 'DELETE' }),
  contentUrl: (attachmentId: string) => `/api/tickets/attachments/${attachmentId}/content`,
};

export const commentApi = {
  list: (ticketId: string) =>
    request<{ items: readonly Comment[] }>(`/api/tickets/${ticketId}/comments`),
  add: (ticketId: string, body: string, internal: boolean) =>
    request<Comment>(`/api/tickets/${ticketId}/comments`, {
      method: 'POST',
      body: { body, internal },
    }),
};

export const orgApi = {
  departments: (includeInactive = false) =>
    request<{ items: readonly Department[] }>(
      `/api/org/departments${buildQuery({ includeInactive })}`,
    ),
  createDepartment: (input: CreateDepartmentInput) =>
    request<Department>('/api/org/departments', { method: 'POST', body: input }),
  updateDepartment: (id: string, input: UpdateDepartmentInput) =>
    request<Department>(`/api/org/departments/${id}`, { method: 'PATCH', body: input }),
  jobRoles: (includeInactive = false) =>
    request<{ items: readonly JobRole[] }>(`/api/org/job-roles${buildQuery({ includeInactive })}`),
  createJobRole: (input: CreateJobRoleInput) =>
    request<JobRole>('/api/org/job-roles', { method: 'POST', body: input }),
  updateJobRole: (id: string, input: UpdateJobRoleInput) =>
    request<JobRole>(`/api/org/job-roles/${id}`, { method: 'PATCH', body: input }),
  projects: (includeInactive = false) =>
    request<{ items: readonly Project[] }>(`/api/org/projects${buildQuery({ includeInactive })}`),
};

export interface ManagedUser {
  readonly id: string;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly jobRoleId: string;
  readonly jobRoleName: string;
  readonly homeDepartmentId: string | null;
  readonly homeDepartmentName: string | null;
  readonly isActive: boolean;
  readonly isAssignable: boolean;
  readonly overlays: readonly AccessOverlay[];
}

export interface ManagedUserDetail extends ManagedUser {
  readonly access: readonly {
    readonly departmentId: string;
    readonly slug: string;
    readonly name: string;
    readonly level: AccessLevel;
  }[];
}

export interface UserListFilters {
  readonly search?: string;
  readonly departmentId?: string;
  readonly isActive?: 'true' | 'false';
  readonly page?: number;
  readonly pageSize?: number;
}

export const userApi = {
  directory: () => request<{ items: readonly DirectoryUser[] }>('/api/users/directory'),
  assignable: (departmentId: string) =>
    request<{ items: readonly AssignableUser[] }>(
      `/api/users/assignable${buildQuery({ departmentId })}`,
    ),
  list: (filters: UserListFilters) =>
    request<{ items: readonly ManagedUser[]; meta: PageMeta }>(
      `/api/users${buildQuery({ ...filters })}`,
    ),
  get: (id: string) => request<ManagedUserDetail>(`/api/users/${id}`),
  create: (input: CreateUserInput) =>
    request<ManagedUserDetail>('/api/users', { method: 'POST', body: input }),
  update: (id: string, input: UpdateUserInput) =>
    request<ManagedUserDetail>(`/api/users/${id}`, { method: 'PATCH', body: input }),
  resetPassword: (id: string, input: ResetUserPasswordInput) =>
    request<{ ok: true }>(`/api/users/${id}/password`, { method: 'POST', body: input }),
};

export interface DashboardReport {
  readonly range: { readonly from: string; readonly to: string; readonly days: number };
  readonly headline: {
    readonly open: number;
    readonly onHold: number;
    readonly unassigned: number;
    readonly createdInRange: number;
    readonly closedInRange: number;
    readonly createdPreviousRange: number;
    readonly closedPreviousRange: number;
    readonly averageDaysToClose: number | null;
    readonly medianDaysToClose: number | null;
    readonly averageOpenAgeDays: number | null;
    readonly staleCount: number;
  };
  readonly byDepartment: ReadonlyArray<{
    readonly departmentId: string;
    readonly name: string;
    readonly open: number;
    readonly onHold: number;
    readonly closedInRange: number;
  }>;
  readonly byAssignee: ReadonlyArray<{
    readonly userId: string;
    readonly name: string;
    readonly open: number;
    readonly closedInRange: number;
  }>;
  readonly byBoard: ReadonlyArray<{
    readonly boardId: string;
    readonly name: string;
    readonly open: number;
    readonly closedInRange: number;
  }>;
  readonly trend: ReadonlyArray<{
    readonly day: string;
    readonly created: number;
    readonly closed: number;
  }>;
  readonly attention: ReadonlyArray<{
    readonly id: string;
    readonly number: number;
    readonly title: string;
    readonly daysQuiet: number;
  }>;
}

export const reportApi = {
  dashboard: (days: number) =>
    request<DashboardReport>(`/api/reports/dashboard${buildQuery({ days })}`),
};

export interface Notification {
  readonly id: string;
  readonly type: string;
  readonly title: string;
  readonly body: string;
  readonly ticketId: string;
  readonly readAt: string | null;
  readonly createdAt: string;
}

export const notificationApi = {
  list: () => request<{ items: readonly Notification[]; unread: number }>('/api/notifications'),
  markRead: (id: string) =>
    request<void>(`/api/notifications/${id}/read`, { method: 'PATCH' }),
  markAllRead: () => request<void>('/api/notifications/read-all', { method: 'PATCH' }),
};
